import { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { onEnter } from '../lib/onEnter';
import Button from './Button';
import styles from './DevMode.module.scss';
import FormInput from './FormInput';

const LOCAL_STORAGE_KEY = 'dev_mode_settings';

export interface DevModeSettings {
  apiOverride?: string;
}

export const DevModeContext = createContext({} as DevModeSettings);

const DevModeInput: FC<{ label: string; onSave: (v: string) => void; value?: string }> = ({ label, onSave, value }) => {
  const [actualValue, setValue] = useState(value);
  useEffect(() => {
    setValue(value);
  }, [value]);

  const timeout = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    timeout.current = setTimeout(() => onSave(actualValue || ''), 600);
  }, [actualValue, onSave]);

  return <FormInput value={actualValue} label={label} onChange={(e) => setValue(e)} />;
};

export const DevModeProvider: FC = ({ children }) => {
  const defaults = localStorage.getItem(LOCAL_STORAGE_KEY);
  const [settings, setSettings] = useState<DevModeSettings>(defaults ? JSON.parse(defaults) : {});
  const [showDevMode, setShowDevMode] = useState(false);
  const closeModal = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowDevMode(false);
    }
  }, []);

  useEffect(() => {
    if (showDevMode) {
      window.addEventListener('keydown', closeModal);
    } else {
      window.removeEventListener('keydown', closeModal);
    }
  }, [closeModal, showDevMode]);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const enabled = useMemo(() => Object.values(settings).some((a) => a), [settings]);

  if (process.env.REACT_APP_ENABLE_DEV_MODE !== 'true') {
    return <DevModeContext.Provider value={{}}>{children}</DevModeContext.Provider>;
  }

  return (
    <DevModeContext.Provider value={settings}>
      <div className={cx(styles.toggle, { [styles.enabled]: enabled })} onClick={() => setShowDevMode((a) => !a)} onKeyDown={onEnter(() => setShowDevMode(true))} role="button" tabIndex={0}>
        {enabled && <span className={styles.warning}>OVERRIDES ENABLED </span>}
        {'</>'}
      </div>
      {showDevMode && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <h1>Dev Mode</h1>
            <DevModeInput label="API Override" onSave={(v) => setSettings((s) => ({ ...s, apiOverride: v }))} value={settings.apiOverride} />
            <div className={styles.footer}>
              <Button onClick={() => setShowDevMode(false)} active>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      {children}
    </DevModeContext.Provider>
  );
};

export const useDevMode = (): DevModeSettings => useContext(DevModeContext);
